import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import FormRenderer from './FormRenderer';
import { validateSchema } from './schema';
import { formTemplateApi } from '../../services/formTemplates';
import { syncService } from '../../services/SyncService';
import { moveSection, moveOption, removeOption, mapErrorsToFields } from './builderSchema';
import { ReticleSignal, signal } from '../../reticle';

export const addSection = (schema, section) => ({ ...schema, sections: [...schema.sections, section] });
export const addField = (schema, sectionId, field) => ({
  ...schema,
  sections: schema.sections.map(section => section.id === sectionId ? { ...section, fields: [...section.fields, field] } : section),
});
export const updateField = (schema, sectionId, fieldId, changes) => ({
  ...schema,
  sections: schema.sections.map(section => section.id === sectionId ? {
    ...section, fields: section.fields.map(field => field.id === fieldId ? { ...field, ...changes } : field),
  } : section),
});
export const moveItem = (schema, sectionId, from, to) => ({
  ...schema,
  sections: schema.sections.map(section => {
    if (section.id !== sectionId) return section;
    const fields = [...section.fields];
    const [item] = fields.splice(from, 1);
    fields.splice(to, 0, item);
    return { ...section, fields };
  }),
});
export const removeItem = (schema, sectionId, fieldId = null) => ({
  ...schema,
  sections: fieldId
    ? schema.sections.map(section => section.id === sectionId ? { ...section, fields: section.fields.filter(field => field.id !== fieldId) } : section)
    : schema.sections.filter(section => section.id !== sectionId),
});

const generateUUID = () => (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function')
  ? crypto.randomUUID()
  : `id-${Math.random().toString(36).substring(2, 11)}`;

const newSection = () => ({ id: generateUUID(), title: '', description: '', fields: [] });
const newField = type => ({
  id: generateUUID(), type, label: '', help_text: '', required: false, validation: {},
  ...(['radio', 'checkbox', 'select'].includes(type) ? { options: [{ id: generateUUID(), label: 'Option 1' }] } : {}),
});

export default function FormBuilder({ token, family, initialDraft, onClose }) {
  const [schema, setSchema] = useState(initialDraft.schema || { schema_version: 1, sections: [] });
  const [serverDraft, setServerDraft] = useState(initialDraft);
  const [name, setName] = useState(family.name || '');
  const [description, setDescription] = useState(family.description || '');
  const [specialty, setSpecialty] = useState(family.specialty || '');

  // Confirmed baselines — updated after each successful save
  const confirmedSchemaRef = useRef(JSON.stringify(initialDraft.schema || { schema_version: 1, sections: [] }));
  const confirmedNameRef = useRef(family.name || '');
  const confirmedDescRef = useRef(family.description || '');
  const confirmedSpecRef = useRef(family.specialty || '');
  const confirmedFamilyUpdatedAtRef = useRef(family.updated_at || '');

  const [isPublishing, setIsPublishing] = useState(false);
  const publishBufferRef = useRef(null);
  const [errors, setErrors] = useState([]);
  const [saveStatus, setSaveStatus] = useState('idle'); // idle | saving | saved | failed | conflict
  const [savedAt, setSavedAt] = useState(null);
  const [preview, setPreview] = useState(false);
  const [conflict, setConflict] = useState('');
  const [previewAnswers, setPreviewAnswers] = useState({});

  const currentSchemaJson = JSON.stringify(schema);
  const isDirty = currentSchemaJson !== confirmedSchemaRef.current
    || name !== confirmedNameRef.current
    || description !== confirmedDescRef.current
    || specialty !== confirmedSpecRef.current;

  // Map errors to fields for inline display
  const fieldErrorMap = mapErrorsToFields(errors);

  // beforeunload guard — only while dirty
  useEffect(() => {
    if (!isDirty) return undefined;
    const handler = e => { e.preventDefault(); };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  const handleClose = () => {
    if (isDirty && !window.confirm('You have unsaved changes. Discard changes and leave?')) {
      return;
    }
    onClose();
  };

  const save = async () => {
    setSaveStatus('saving');
    setConflict('');
    try {
      if (name !== confirmedNameRef.current || description !== confirmedDescRef.current || specialty !== confirmedSpecRef.current) {
        const metaPayload = { name, description, specialty, expected_updated_at: confirmedFamilyUpdatedAtRef.current };
        const updatedFamily = await formTemplateApi.updateMetadata(family.id, metaPayload, token);
        // Advance confirmed family timestamp
        confirmedFamilyUpdatedAtRef.current = updatedFamily.updated_at || confirmedFamilyUpdatedAtRef.current;
        confirmedNameRef.current = name;
        confirmedDescRef.current = description;
        confirmedSpecRef.current = specialty;
      }
      const saved = await formTemplateApi.saveDraft(serverDraft.id, {
        schema, expected_updated_at: serverDraft.updated_at,
      }, token);
      setServerDraft(saved);
      setSchema(saved.schema);
      // Update confirmed schema baseline
      confirmedSchemaRef.current = JSON.stringify(saved.schema);
      setSaveStatus('saved');
      setSavedAt(new Date());
      toast.success('Draft saved');
      return saved;
    } catch (error) {
      if (error.response?.status === 409) {
        setSaveStatus('conflict');
        setConflict(error.response.data?.detail || 'Template draft changed; reload before saving');
      } else {
        setSaveStatus('failed');
        toast.error(error.response?.data?.detail || 'Unable to save template draft');
      }
      return null;
    }
  };

  const focusFirstError = nextErrors => {
    if (!nextErrors.length) return;
    for (const first of nextErrors) {
      if (first.path) {
        // Option label error: sections.0.fields.0.options.1.label
        const optMatch = first.path.match(/^sections\.(\d+)\.fields\.(\d+)\.options\.(\d+)\.label$/);
        if (optMatch) {
          const sIdx = parseInt(optMatch[1], 10);
          const fIdx = parseInt(optMatch[2], 10);
          const oIdx = parseInt(optMatch[3], 10);
          const option = schema.sections[sIdx]?.fields[fIdx]?.options[oIdx];
          if (option) {
            const el = document.getElementById(`opt-${option.id}-label`);
            if (el) { el.scrollIntoView?.({ behavior: 'smooth', block: 'center' }); el.focus(); return; }
          }
        }
        // Validation error: sections.0.fields.0.validation
        const valMatch = first.path.match(/^sections\.(\d+)\.fields\.(\d+)\.validation(?:\.(min|max|min_length|max_length))?/);
        if (valMatch) {
          const sIdx = parseInt(valMatch[1], 10);
          const fIdx = parseInt(valMatch[2], 10);
          const subProp = valMatch[3];
          const field = schema.sections[sIdx]?.fields[fIdx];
          if (field) {
            const el = (subProp && document.getElementById(`field-${field.id}-${subProp}`))
              || document.getElementById(`field-${field.id}-validation-error`)
              || document.getElementById(`field-${field.id}-min`)
              || document.getElementById(`${field.id}-label`);
            if (el) { el.scrollIntoView?.({ behavior: 'smooth', block: 'center' }); el.focus(); return; }
          }
        }
        // Empty section error: sections.0.fields
        const secFieldsMatch = first.path.match(/^sections\.(\d+)\.fields$/);
        if (secFieldsMatch) {
          const sIdx = parseInt(secFieldsMatch[1], 10);
          const section = schema.sections[sIdx];
          if (section) {
            const el = document.getElementById(`${section.id}-fields-error`) || document.getElementById(`${section.id}-title`);
            if (el) { el.scrollIntoView?.({ behavior: 'smooth', block: 'center' }); el.focus(); return; }
          }
        }
        // Section title error: sections.0.title
        const secTitleMatch = first.path.match(/^sections\.(\d+)\.title$/);
        if (secTitleMatch) {
          const sIdx = parseInt(secTitleMatch[1], 10);
          const section = schema.sections[sIdx];
          if (section) {
            const el = document.getElementById(`${section.id}-title`);
            if (el) { el.scrollIntoView?.({ behavior: 'smooth', block: 'center' }); el.focus(); return; }
          }
        }
      }
      if (first.fieldId) {
        const el = document.getElementById(`${first.fieldId}-label`);
        if (el) {
          el.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
          el.focus();
          return;
        }
      }
    }
  };

  const publish = async () => {
    if (isPublishing) return;
    const nextErrors = validateSchema(schema);
    setErrors(nextErrors);
    if (nextErrors.length) {
      focusFirstError(nextErrors);
      return;
    }
    publishBufferRef.current = JSON.stringify({ name, description, specialty, schema });
    setIsPublishing(true);
    try {
      const saved = await save();
      if (!saved) return;
      const published = await formTemplateApi.publish(saved.id, token);
      toast.success(`Published version ${published.version_number}`);
      try {
        if (!syncService.token && token) syncService.token = token;
        const synchronized = await syncService.pullData();
        if (synchronized) {
          signal(ReticleSignal.TEMPLATE_PUBLISHED, {
            templateId: family.id,
            versionId: published.id,
            ownerType: family.owner_type,
            ownerId: family.owner_id,
          });
        } else {
          console.error('Failed to sync templates after publish');
        }
      } catch (syncErr) {
        console.error('Failed to sync templates after publish:', syncErr);
      }
      const currentBuffer = JSON.stringify({ name, description, specialty, schema });
      if (currentBuffer !== publishBufferRef.current) {
        toast.info('Publish completed. Additional unsaved changes remain in editor.');
      } else {
        onClose();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to publish version');
    } finally {
      setIsPublishing(false);
    }
  };

  const handleDiscardDraft = async () => {
    if (!window.confirm('Discard this draft? Unsaved modifications will be permanently deleted.')) return;
    try {
      await formTemplateApi.discardDraft(serverDraft.id, token);
      toast.success('Draft discarded');
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to discard draft');
    }
  };

  const updateSection = (sectionId, changes) => setSchema(current => ({
    ...current, sections: current.sections.map(section => section.id === sectionId ? { ...section, ...changes } : section),
  }));

  // Helper to get inline errors for a given key
  const getFieldErrors = key => fieldErrorMap.get(key) || [];

  return (
    <section className="space-y-6" aria-labelledby="builder-title">
      <header className="flex items-center justify-between border-b pb-4">
        <div>
          <Button variant="ghost" onClick={handleClose} className="mb-2">
            ← Back to templates
          </Button>
          <div className="flex items-center gap-3">
            <h1 id="builder-title" className="text-2xl font-bold">Edit {family.name}</h1>
            {isDirty && (
              <span className="text-xs bg-amber-100 text-amber-800 px-2 py-0.5 rounded">
                Unsaved changes
              </span>
            )}
          </div>
        </div>
        <Button
          variant="outline"
          disabled={isPublishing || saveStatus === 'saving'}
          className="text-red-600 border-red-200 hover:bg-red-50"
          onClick={handleDiscardDraft}
        >
          Discard draft
        </Button>
      </header>

      <fieldset disabled={isPublishing || saveStatus === 'saving'} className="space-y-6 border-0 p-0 m-0 min-w-0">
        {/* Metadata editing */}
      <div className="p-4 border rounded-lg bg-slate-50 space-y-3">
        <h2 className="font-semibold text-sm text-slate-700">Template Metadata</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <Label htmlFor="builder-meta-name">Name</Label>
            <Input id="builder-meta-name" value={name} onChange={e => setName(e.target.value)} />
          </div>
          <div>
            <Label htmlFor="builder-meta-desc">Description</Label>
            <Input id="builder-meta-desc" value={description} onChange={e => setDescription(e.target.value)} />
          </div>
          <div>
            <Label htmlFor="builder-meta-spec">Specialty</Label>
            <Input id="builder-meta-spec" value={specialty} onChange={e => setSpecialty(e.target.value)} />
          </div>
        </div>
      </div>

      {conflict && (
        <div role="alert" className="border border-amber-300 bg-amber-50 p-4 rounded-lg text-amber-900 font-medium">
          {conflict}
        </div>
      )}

      {errors.length > 0 && (
        <div role="alert" className="border border-red-200 bg-red-50 p-4 rounded-lg text-red-900">
          <h2 className="font-semibold text-sm mb-2">Fix these issues before publishing:</h2>
          <ul className="list-disc list-inside text-xs space-y-1">
            {errors.map(item => (
              <li key={`${item.path}-${item.message}`}>{item.message}</li>
            ))}
          </ul>
        </div>
      )}

      {schema.sections.map((section, sectionIndex) => {
        const sectionTitleErrors = getFieldErrors(`sections.${sectionIndex}.title`);
        const sectionDescErrors = getFieldErrors(`sections.${sectionIndex}.description`);
        const sectionFieldsErrors = getFieldErrors(`sections.${sectionIndex}.fields`);
        return (
        <article key={section.id} className="rounded-xl border p-5 bg-white shadow-sm space-y-4">
          {sectionFieldsErrors.length > 0 && (
            <p id={`${section.id}-fields-error`} tabIndex={-1} role="alert" className="text-xs text-red-600 font-medium focus:outline-none focus:ring-1 focus:ring-red-500 rounded p-1">
              {sectionFieldsErrors[0]}
            </p>
          )}
          <div className="space-y-3">
            {/* Section ordering controls */}
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                Section {sectionIndex + 1}
              </span>
              <div className="flex gap-1">
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={sectionIndex === 0}
                  onClick={() => setSchema(current => moveSection(current, sectionIndex, sectionIndex - 1))}
                >
                  Move section up
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={sectionIndex === schema.sections.length - 1}
                  onClick={() => setSchema(current => moveSection(current, sectionIndex, sectionIndex + 1))}
                >
                  Move section down
                </Button>
              </div>
            </div>
            <div>
              <Label htmlFor={`${section.id}-title`}>Section title</Label>
              <Input
                id={`${section.id}-title`}
                value={section.title}
                onChange={event => updateSection(section.id, { title: event.target.value })}
                aria-describedby={sectionTitleErrors.length ? `${section.id}-title-error` : undefined}
              />
              {sectionTitleErrors.length > 0 && (
                <p id={`${section.id}-title-error`} role="alert" className="text-xs text-red-600 mt-1">
                  {sectionTitleErrors[0]}
                </p>
              )}
            </div>
            <div>
              <Label htmlFor={`${section.id}-description`}>Section description</Label>
              <Textarea
                id={`${section.id}-description`}
                value={section.description || ''}
                onChange={event => updateSection(section.id, { description: event.target.value })}
                aria-describedby={sectionDescErrors.length ? `${section.id}-desc-error` : undefined}
              />
              {sectionDescErrors.length > 0 && (
                <p id={`${section.id}-desc-error`} role="alert" className="text-xs text-red-600 mt-1">
                  {sectionDescErrors[0]}
                </p>
              )}
            </div>
          </div>

          {section.fields.map((field, fieldIndex) => {
            const fieldErrors = getFieldErrors(field.id);
            const setValidation = changes => setSchema(current => updateField(current, section.id, field.id, {
              validation: { ...(field.validation || {}), ...changes },
            }));
            return (
              <div key={field.id} className="mt-4 rounded-lg bg-slate-50 p-4 border border-slate-200 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                    {field.type.replace('_', ' ')}
                  </span>
                  <div className="flex gap-1">
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={fieldIndex === 0}
                      onClick={() => setSchema(current => moveItem(current, section.id, fieldIndex, fieldIndex - 1))}
                    >
                      Move up
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={fieldIndex === section.fields.length - 1}
                      onClick={() => setSchema(current => moveItem(current, section.id, fieldIndex, fieldIndex + 1))}
                    >
                      Move down
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-red-600 hover:text-red-700"
                      onClick={() => window.confirm('Remove this field?') && setSchema(current => removeItem(current, section.id, field.id))}
                    >
                      Remove
                    </Button>
                  </div>
                </div>
                <div>
                  <Label htmlFor={`${field.id}-label`}>Field label</Label>
                  <Input
                    id={`${field.id}-label`}
                    value={field.label}
                    onChange={event => setSchema(current => updateField(current, section.id, field.id, { label: event.target.value }))}
                    aria-describedby={fieldErrors.length ? `${field.id}-error` : undefined}
                  />
                  {fieldErrors.length > 0 && (
                    <p id={`${field.id}-error`} role="alert" className="text-xs text-red-600 mt-1">
                      {fieldErrors[0]}
                    </p>
                  )}
                </div>
                <div>
                  <Label htmlFor={`${field.id}-help`}>Help text</Label>
                  <Input
                    id={`${field.id}-help`}
                    value={field.help_text || ''}
                    onChange={event => setSchema(current => updateField(current, section.id, field.id, { help_text: event.target.value }))}
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox
                    id={`${field.id}-required`}
                    checked={field.required}
                    onCheckedChange={required => setSchema(current => updateField(current, section.id, field.id, { required }))}
                  />
                  <Label htmlFor={`${field.id}-required`}>Required</Label>
                </div>

                {['radio', 'checkbox', 'select'].includes(field.type) && (
                  <div className="space-y-2 pt-2 border-t">
                    <Label>Options</Label>
                    {(field.options || []).map((option, optionIndex) => {
                      const optErrors = getFieldErrors(`sections.${sectionIndex}.fields.${fieldIndex}.options.${optionIndex}.label`);
                      return (
                        <div key={option.id} className="space-y-1">
                          <div className="flex gap-2 items-center">
                            <Input
                              id={`opt-${option.id}-label`}
                              aria-label={`Option ${optionIndex + 1}`}
                              value={option.label}
                              aria-describedby={optErrors.length ? `opt-${option.id}-error` : undefined}
                              onChange={event => setSchema(current => updateField(current, section.id, field.id, {
                                options: field.options.map(item => item.id === option.id ? { ...item, label: event.target.value } : item),
                              }))}
                            />
                            <Button
                              type="button"
                              size="sm"
                              variant="ghost"
                              disabled={optionIndex === 0}
                              onClick={() => setSchema(current => moveOption(current, section.id, field.id, optionIndex, optionIndex - 1))}
                              aria-label={`Move option ${optionIndex + 1} up`}
                            >
                              ↑
                            </Button>
                            <Button
                              type="button"
                              size="sm"
                              variant="ghost"
                              disabled={optionIndex === (field.options || []).length - 1}
                              onClick={() => setSchema(current => moveOption(current, section.id, field.id, optionIndex, optionIndex + 1))}
                              aria-label={`Move option ${optionIndex + 1} down`}
                            >
                              ↓
                            </Button>
                            <Button
                              type="button"
                              size="sm"
                              variant="ghost"
                              className="text-red-600 hover:text-red-700"
                              disabled={(field.options || []).length <= 1}
                              onClick={() => setSchema(current => removeOption(current, section.id, field.id, option.id))}
                              aria-label={`Remove option ${optionIndex + 1}`}
                            >
                              ×
                            </Button>
                          </div>
                          {optErrors.length > 0 && (
                            <p id={`opt-${option.id}-error`} role="alert" className="text-xs text-red-600">
                              {optErrors[0]}
                            </p>
                          )}
                        </div>
                      );
                    })}
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => setSchema(current => updateField(current, section.id, field.id, {
                        options: [...(field.options || []), { id: generateUUID(), label: `Option ${(field.options || []).length + 1}` }],
                      }))}
                    >
                      Add option
                    </Button>
                  </div>
                )}

                {field.type === 'number' && (
                  <div className="grid grid-cols-2 gap-3 pt-2 border-t">
                    <div>
                      <Label htmlFor={`field-${field.id}-min`}>Minimum</Label>
                      <Input
                        id={`field-${field.id}-min`}
                        aria-label="Minimum"
                        type="number"
                        value={field.validation?.min ?? ''}
                        onChange={event => setValidation({ min: event.target.value === '' ? undefined : Number(event.target.value) })}
                      />
                    </div>
                    <div>
                      <Label htmlFor={`field-${field.id}-max`}>Maximum</Label>
                      <Input
                        id={`field-${field.id}-max`}
                        aria-label="Maximum"
                        type="number"
                        value={field.validation?.max ?? ''}
                        onChange={event => setValidation({ max: event.target.value === '' ? undefined : Number(event.target.value) })}
                      />
                    </div>
                  </div>
                )}

                {['short_text', 'long_text'].includes(field.type) && (
                  <div className="grid grid-cols-2 gap-3 pt-2 border-t">
                    <div>
                      <Label htmlFor={`field-${field.id}-min_length`}>Minimum length</Label>
                      <Input
                        id={`field-${field.id}-min_length`}
                        aria-label="Minimum length"
                        type="number"
                        value={field.validation?.min_length ?? 0}
                        onChange={event => setValidation({ min_length: Number(event.target.value) })}
                      />
                    </div>
                    <div>
                      <Label htmlFor={`field-${field.id}-max_length`}>Maximum length</Label>
                      <Input
                        id={`field-${field.id}-max_length`}
                        aria-label="Maximum length"
                        type="number"
                        max={field.type === 'short_text' ? 500 : 10000}
                        value={field.validation?.max_length ?? (field.type === 'short_text' ? 500 : 10000)}
                        onChange={event => setValidation({ max_length: Number(event.target.value) })}
                      />
                    </div>
                  </div>
                )}

                {getFieldErrors(`sections.${sectionIndex}.fields.${fieldIndex}.validation`).length > 0 && (
                  <p id={`field-${field.id}-validation-error`} tabIndex={-1} role="alert" className="text-xs text-red-600 focus:outline-none focus:ring-1 focus:ring-red-500 rounded p-1">
                    {getFieldErrors(`sections.${sectionIndex}.fields.${fieldIndex}.validation`)[0]}
                  </p>
                )}

                {field.type === 'checkbox' && (
                  <div className="grid grid-cols-2 gap-3 pt-2 border-t">
                    <div>
                      <Label>Minimum selections</Label>
                      <Input
                        aria-label="Minimum selections"
                        type="number"
                        value={field.validation?.min_selections ?? 0}
                        onChange={event => setValidation({ min_selections: Number(event.target.value) })}
                      />
                    </div>
                    <div>
                      <Label>Maximum selections</Label>
                      <Input
                        aria-label="Maximum selections"
                        type="number"
                        value={field.validation?.max_selections ?? (field.options || []).length}
                        onChange={event => setValidation({ max_selections: Number(event.target.value) })}
                      />
                    </div>
                  </div>
                )}

                {field.type === 'date' && (
                  <div className="grid grid-cols-2 gap-3 pt-2 border-t">
                    <div>
                      <Label>Earliest date</Label>
                      <Input
                        aria-label="Earliest date"
                        type="date"
                        value={field.validation?.earliest ?? ''}
                        onChange={event => setValidation({ earliest: event.target.value })}
                      />
                    </div>
                    <div>
                      <Label>Latest date</Label>
                      <Input
                        aria-label="Latest date"
                        type="date"
                        value={field.validation?.latest ?? ''}
                        onChange={event => setValidation({ latest: event.target.value })}
                      />
                    </div>
                  </div>
                )}
              </div>
            );
          })}

          <div className="flex items-center justify-between pt-3">
            <select
              aria-label={`Add field to ${section.title || `section ${sectionIndex + 1}`}`}
              className="h-9 w-56 rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring"
              value=""
              onChange={event => {
                if (event.target.value) {
                  setSchema(current => addField(current, section.id, newField(event.target.value)));
                }
              }}
            >
              <option value="" disabled>Add field</option>
              {['short_text', 'long_text', 'number', 'radio', 'checkbox', 'select', 'date'].map(type => (
                <option key={type} value={type}>{type.replace('_', ' ')}</option>
              ))}
            </select>

            <Button
              variant="ghost"
              className="text-red-600 hover:text-red-700"
              onClick={() => window.confirm('Remove this section?') && setSchema(current => removeItem(current, section.id))}
            >
              Remove section
            </Button>
          </div>
        </article>
        );
      })}

      <Button disabled={isPublishing || saveStatus === 'saving'} onClick={() => setSchema(current => addSection(current, newSection()))}>Add section</Button>

      {/* Persistent save status */}
      <div className="flex flex-wrap items-center gap-3 pt-4 border-t">
        <Button disabled={saveStatus === 'saving' || isPublishing} onClick={save}>Save draft</Button>
        <Button variant="outline" disabled={isPublishing} onClick={() => setPreview(value => !value)}>Preview form</Button>
        <Button disabled={saveStatus === 'saving' || isPublishing} onClick={publish}>
          {isPublishing ? 'Publishing…' : 'Publish version'}
        </Button>
        <span className="text-xs text-slate-500 ml-auto">
          {saveStatus === 'saving' && 'Saving…'}
          {saveStatus === 'saved' && savedAt && `Saved at ${savedAt.toLocaleTimeString()}`}
          {saveStatus === 'failed' && 'Save failed — changes are still in this browser'}
          {saveStatus === 'conflict' && 'Conflict — reload before saving'}
        </span>
      </div>
      </fieldset>

      {preview && (
        <div className="rounded-xl border p-6 bg-white shadow-sm space-y-4">
          <h2 className="text-lg font-semibold">Form Preview</h2>
          <FormRenderer
            schema={schema}
            answers={previewAnswers}
            onChange={(id, value) => setPreviewAnswers(current => ({ ...current, [id]: value }))}
            errors={[]}
          />
        </div>
      )}
    </section>
  );
}

FormBuilder.propTypes = {
  token: PropTypes.string.isRequired,
  family: PropTypes.object.isRequired,
  initialDraft: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
};
